// src/pages/settings/ImageDropInput.tsx — input drag & drop image + recadreur, réutilisé par tous les formulaires

import { useState, useEffect, useCallback, useRef } from "react";

// Accepted image formats and limits
const ACCEPTED_TYPES = ["image/jpeg","image/png","image/svg+xml"];

// ── Image cropper ─────────────────────────────────────────────────────────────
export interface CropTarget { w: number; h: number; }

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const CROP_MAX_W = 480;
const CROP_MAX_H = Math.round(CROP_MAX_W * 3 / 4); // 360 — ratio 4:3 de la photo site @ 150 dpi
const HANDLE_PX  = 10;

type DragMode = "move" | "nw" | "ne" | "sw" | "se";
const CORNER_HANDLES: { id: "nw"|"ne"|"sw"|"se"; pos: React.CSSProperties; cursor: string }[] = [
  { id:"nw", pos:{ top: -HANDLE_PX/2, left:  -HANDLE_PX/2 }, cursor:"nwse-resize" },
  { id:"ne", pos:{ top: -HANDLE_PX/2, right: -HANDLE_PX/2 }, cursor:"nesw-resize" },
  { id:"sw", pos:{ bottom: -HANDLE_PX/2, left:  -HANDLE_PX/2 }, cursor:"nesw-resize" },
  { id:"se", pos:{ bottom: -HANDLE_PX/2, right: -HANDLE_PX/2 }, cursor:"nwse-resize" },
];

function ImageCropper({
  src, defaultTarget, canEditTarget, onConfirm, onCancel,
}: {
  src:           string;
  defaultTarget: CropTarget;
  canEditTarget: boolean;
  onConfirm:     (dataUrl: string, target: CropTarget) => void;
  onCancel:      () => void;
}) {
  const [natW,    setNatW]    = useState(0);
  const [natH,    setNatH]    = useState(0);
  const [targetW, setTargetW] = useState(defaultTarget.w);
  const [targetH, setTargetH] = useState(defaultTarget.h);
  const [box,     setBox]     = useState({ x:0, y:0, w:0, h:0 });
  const initialized   = useRef(false);
  const prevTargetRef = useRef({ w: defaultTarget.w, h: defaultTarget.h });

  // Load natural dimensions
  useEffect(() => {
    const img = new Image();
    img.onload = () => { setNatW(img.naturalWidth); setNatH(img.naturalHeight); };
    img.src = src;
  }, [src]);

  // Initialize crop box centered at target aspect ratio (once)
  useEffect(() => {
    if (!natW || !natH || initialized.current) return;
    initialized.current = true;
    const ar = targetW / targetH;
    let w = natW, h = natH;
    if (w / h > ar) { w = h * ar; } else { h = w / ar; }
    w = Math.min(w, natW); h = Math.min(h, natH);
    setBox({ x: (natW - w) / 2, y: (natH - h) / 2, w, h });
  }, [natW, natH, targetW, targetH]);

  // Adjust box aspect ratio when target dims change (keep center)
  useEffect(() => {
    if (!natW || !box.w) return;
    if (prevTargetRef.current.w === targetW && prevTargetRef.current.h === targetH) return;
    prevTargetRef.current = { w: targetW, h: targetH };
    const ar = targetW / targetH;
    const cx = box.x + box.w / 2, cy = box.y + box.h / 2;
    let w = box.w, h = w / ar;
    if (h > natH) { h = natH; w = h * ar; }
    if (w > natW) { w = natW; h = w / ar; }
    setBox({
      x: clamp(cx - w / 2, 0, natW - w),
      y: clamp(cy - h / 2, 0, natH - h),
      w, h,
    });
  }, [targetW, targetH, natW, natH, box.w, box.x, box.y, box.h]);

  const scale = (natW && natH) ? Math.min(CROP_MAX_W / natW, CROP_MAX_H / natH, 1) : 1;
  const dispW = Math.round(natW * scale);
  const dispH = Math.round(natH * scale);

  // Global drag state stored in a ref to avoid stale closure in document listeners
  const dragRef = useRef<{
    mode: DragMode; sx: number; sy: number;
    sb: typeof box; scale: number;
    nw: number; nh: number; ar: number;
  } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (e.clientX - d.sx) / d.scale;
      const dy = (e.clientY - d.sy) / d.scale;
      const { sb, nw, nh, ar: a } = d;
      setBox(() => {
        let { x, y, w, h } = sb;
        if (d.mode === "move") {
          x = clamp(x + dx, 0, nw - w);
          y = clamp(y + dy, 0, nh - h);
        } else if (d.mode === "se") {
          w = Math.max(40, sb.w + dx); h = w / a;
          if (x + w > nw) { w = nw - x; h = w / a; }
          if (y + h > nh) { h = nh - y; w = h * a; }
        } else if (d.mode === "sw") {
          w = Math.max(40, sb.w - dx); h = w / a;
          x = sb.x + sb.w - w;
          if (x < 0)      { x = 0; w = sb.x + sb.w; h = w / a; }
          if (y + h > nh) { h = nh - y; w = h * a; x = sb.x + sb.w - w; }
        } else if (d.mode === "ne") {
          w = Math.max(40, sb.w + dx); h = w / a;
          y = sb.y + sb.h - h;
          if (x + w > nw) { w = nw - x; h = w / a; y = sb.y + sb.h - h; }
          if (y < 0)      { y = 0; h = sb.y + sb.h; w = h * a; }
        } else if (d.mode === "nw") {
          w = Math.max(40, sb.w - dx); h = w / a;
          x = sb.x + sb.w - w; y = sb.y + sb.h - h;
          if (x < 0) { x = 0; w = sb.x + sb.w; h = w / a; y = sb.y + sb.h - h; }
          if (y < 0) { y = 0; h = sb.y + sb.h; w = h * a; x = sb.x + sb.w - w; }
        }
        return { x: Math.max(0, x), y: Math.max(0, y), w: Math.max(40, w), h: Math.max(30, h) };
      });
    };
    const onUp = () => { dragRef.current = null; };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  }, []); // runs once — uses dragRef which always holds latest values

  const startDrag = (e: React.MouseEvent, mode: DragMode) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { mode, sx: e.clientX, sy: e.clientY,
      sb: { ...box }, scale, nw: natW, nh: natH, ar: targetW / targetH };
  };

  const applyCrop = useCallback(() => {
    const canvas = document.createElement("canvas");
    canvas.width = targetW; canvas.height = targetH;
    const ctx = canvas.getContext("2d")!;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, box.x, box.y, box.w, box.h, 0, 0, targetW, targetH);
      onConfirm(canvas.toDataURL("image/jpeg", 0.88), { w: targetW, h: targetH });
    };
    img.src = src;
  }, [src, box, targetW, targetH, onConfirm]);

  // Redimensionnement sans recadrage — image entière mise à l'échelle cible
  const resizeToTarget = useCallback(() => {
    const canvas = document.createElement("canvas");
    canvas.width = targetW; canvas.height = targetH;
    const ctx = canvas.getContext("2d")!;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, targetW, targetH);
      onConfirm(canvas.toDataURL("image/jpeg", 0.88), { w: targetW, h: targetH });
    };
    img.src = src;
  }, [src, targetW, targetH, onConfirm]);

  // Seuil : image ≥ 150 % de la résolution cible
  const isOversized = natW > 0 && (natW > targetW * 1.5 || natH > targetH * 1.5);

  // Display-space crop box
  const db = { x: Math.round(box.x * scale), y: Math.round(box.y * scale),
               w: Math.round(box.w * scale), h: Math.round(box.h * scale) };

  const handleDim = (field: "w"|"h", raw: string) => {
    const v = parseInt(raw, 10);
    if (!v || v < 10) return;
    if (field === "w") setTargetW(v); else setTargetH(v);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:3000,
      background:"rgba(0,0,0,0.72)", backdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background:"#fff", borderRadius:12, overflow:"hidden",
        boxShadow:"0 16px 60px rgba(0,0,0,0.4)", maxWidth:560, width:"100%" }}>

        {/* Header */}
        <div style={{ padding:"16px 20px 12px", borderBottom:"1px solid #f0f0f0",
          display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <h3 style={{ margin:0, fontSize:15, fontFamily:"var(--ff-title)", fontWeight:900 }}>
              Recadrer la photo
            </h3>
            <p style={{ margin:"3px 0 0", fontSize:11, color:"#888" }}>
              Glissez le cadre ou ses coins · ratio {targetW}:{targetH} verrouillé
            </p>
          </div>
          <button onClick={onCancel}
            style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:"#aaa", lineHeight:1 }}>✕</button>
        </div>

        {/* Bannière redimensionnement — image trop grande ou > 2 Mo */}
        {isOversized && (
          <div style={{ padding:"10px 20px", background:"#fffbea", borderBottom:"1px solid #ffe082",
            display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, color:"#7a5c00", fontFamily:"var(--ff-title)" }}>
              ⚠ Image source {natW}×{natH} px —{" "}
              {Math.max(natW / targetW, natH / targetH).toFixed(1)}× plus grande que la cible.
            </span>
            <button onClick={resizeToTarget} className="btn btn-black"
              style={{ fontSize:11, padding:"4px 12px", flexShrink:0 }}>
              Réduire à {targetW}×{targetH} →
            </button>
          </div>
        )}

        {/* Crop canvas */}
        <div style={{ padding:"16px 20px", background:"#111",
          display:"flex", justifyContent:"center", alignItems:"center", minHeight:120 }}>
          {natW > 0 ? (
            <div style={{ position:"relative", width:dispW, height:dispH, userSelect:"none" }}>
              {/* Source image */}
              <img src={src} alt="" draggable={false}
                style={{ width:dispW, height:dispH, display:"block", pointerEvents:"none" }}/>

              {/* Overlay: 4 darkened panels around the crop box */}
              <div style={{ position:"absolute", top:0, left:0, right:0, height:db.y,
                background:"rgba(0,0,0,0.55)", pointerEvents:"none" }}/>
              <div style={{ position:"absolute", left:0, right:0, top:db.y+db.h, bottom:0,
                background:"rgba(0,0,0,0.55)", pointerEvents:"none" }}/>
              <div style={{ position:"absolute", top:db.y, height:db.h, left:0, width:db.x,
                background:"rgba(0,0,0,0.55)", pointerEvents:"none" }}/>
              <div style={{ position:"absolute", top:db.y, height:db.h, left:db.x+db.w, right:0,
                background:"rgba(0,0,0,0.55)", pointerEvents:"none" }}/>

              {/* Crop box */}
              {db.w > 0 && (
                <div onMouseDown={e => startDrag(e, "move")}
                  style={{ position:"absolute", left:db.x, top:db.y, width:db.w, height:db.h,
                    border:"1.5px solid rgba(255,255,255,0.9)", cursor:"move", boxSizing:"border-box" }}>
                  {/* Rule-of-thirds guide lines */}
                  <div style={{ position:"absolute", inset:0, pointerEvents:"none",
                    backgroundImage:"linear-gradient(to right,rgba(255,255,255,0.18) 1px,transparent 1px),"
                      +"linear-gradient(to bottom,rgba(255,255,255,0.18) 1px,transparent 1px)",
                    backgroundSize:"33.33% 33.33%" }}/>
                  {/* Corner resize handles */}
                  {CORNER_HANDLES.map(({ id, pos, cursor }) => (
                    <div key={id} onMouseDown={e => startDrag(e, id)}
                      style={{ position:"absolute", width:HANDLE_PX, height:HANDLE_PX,
                        background:"#fff", borderRadius:2, cursor, ...pos }}/>
                  ))}
                </div>
              )}

              {/* Dimensions of the crop area in source pixels */}
              <div style={{ position:"absolute", left:db.x, top:Math.min(db.y + db.h + 4, dispH - 16),
                fontSize:10, color:"rgba(255,255,255,0.65)", fontFamily:"var(--ff-title)",
                pointerEvents:"none", whiteSpace:"nowrap" }}>
                {Math.round(box.w)} × {Math.round(box.h)} px sélectionnés
              </div>
            </div>
          ) : (
            <div style={{ color:"#666", fontSize:13 }}>Chargement de l'image…</div>
          )}
        </div>

        {/* Output resolution + action buttons */}
        <div style={{ padding:"14px 20px 16px", borderTop:"1px solid #f0f0f0" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:"#666", fontFamily:"var(--ff-title)", fontWeight:700,
              letterSpacing:0.5, textTransform:"uppercase", flexShrink:0 }}>
              Résolution de sortie
            </span>
            {canEditTarget ? (
              <>
                <input type="number" min={40} max={4096} value={targetW}
                  onChange={e => handleDim("w", e.target.value)}
                  className="field"
                  style={{ width:72, marginBottom:0, textAlign:"center", fontSize:13 }}/>
                <span style={{ color:"#aaa" }}>×</span>
                <input type="number" min={30} max={4096} value={targetH}
                  onChange={e => handleDim("h", e.target.value)}
                  className="field"
                  style={{ width:72, marginBottom:0, textAlign:"center", fontSize:13 }}/>
                <span style={{ fontSize:11, color:"#aaa" }}>px</span>
              </>
            ) : (
              <span style={{ fontSize:13, fontWeight:600 }}>{targetW} × {targetH} px</span>
            )}
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
            <button onClick={onCancel} className="btn btn-ghost">Annuler</button>
            <button onClick={applyCrop} disabled={!natW} className="btn btn-black">
              {natW > 0 && natW <= targetW && natH <= targetH ? "Enregistrer" : "Rogner →"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export function ImageDropInput({ value, onChange, label, height=100, cropTarget, canEditCropTarget=false }:{
  value:string; onChange:(v:string)=>void; label:string; height?:number|string;
  cropTarget?:       CropTarget;
  canEditCropTarget?: boolean;
}) {
  const [drag,    setDrag]    = useState(false);
  const [warning, setWarning] = useState<string|null>(null);
  const [pending, setPending] = useState<string|null>(null);
  const ref = useRef<HTMLInputElement>(null);

  const read = (file:File) => {
    setWarning(null);
    if(!ACCEPTED_TYPES.includes(file.type)){
      setWarning("Format non supporté. Utilisez JPG, PNG ou SVG.");
      return;
    }
    if (cropTarget) {
      // Vérification taille différée : le recadreur proposera un redimensionnement si nécessaire
      const r = new FileReader();
      r.onload = () => setPending(r.result as string);
      r.readAsDataURL(file);
      return;
    }

    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result as string;
      if (file.type === "image/svg+xml") { onChange(dataUrl); return; }
      // No crop — validate dimensions and commit immediately
      const img = new Image();
      img.onload = () => { onChange(dataUrl); };
      img.src = dataUrl;
    };
    r.readAsDataURL(file);
  };

  const pasteFromClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      // Priorité aux types image/* (inclut image/svg+xml si le navigateur l'expose)
      const pairs = items.flatMap(i => i.types.filter(t => t.startsWith("image/")).map(t => ({item:i,type:t})));
      if (pairs.length > 0) {
        const blob = await pairs[0].item.getType(pairs[0].type);
        read(new File([blob], "pasted", { type: pairs[0].type }));
        return;
      }
      // Fallback : texte brut — SVG inline ou URL vers une image
      for (const item of items) {
        if (item.types.includes("text/plain")) {
          const text = await (await item.getType("text/plain")).text();
          const trimmed = text.trim();
          if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
            read(new File([new Blob([text], { type: "image/svg+xml" })], "pasted.svg", { type: "image/svg+xml" }));
            return;
          }
          try {
            const url = new URL(trimmed);
            const ext = url.pathname.split(".").pop()?.toLowerCase() ?? "";
            if (["svg", "jpg", "jpeg", "png"].includes(ext)) {
              onChange(trimmed);
              return;
            }
          } catch { /* pas une URL */ }
        }
      }
      setWarning("Aucune image trouvée dans le presse-papiers.");
    } catch { setWarning("Accès au presse-papiers refusé."); }
  };

  const stretch = height === "100%";
  return (
    <>
      {/* ImageCropper overlay — rendered outside the normal flow via fragment */}
      {pending && cropTarget && (
        <ImageCropper
          src={pending}
          defaultTarget={cropTarget}
          canEditTarget={canEditCropTarget}
          onConfirm={(dataUrl, _target) => { onChange(dataUrl); setPending(null); }}
          onCancel={() => setPending(null)}
        />
      )}

      <div style={stretch ? {display:"flex",flexDirection:"column",height:"100%"} : {}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <label className="label" style={{marginBottom:0}}>{label}</label>
          <button type="button" onClick={pasteFromClipboard}
            style={{background:"#f4f4f4",border:"1px solid #ccc",borderRadius:5,padding:"3px 10px",
              cursor:"pointer",fontSize:11,color:"#444",fontFamily:"var(--ff-title)",whiteSpace:"nowrap",
              display:"flex",alignItems:"center",gap:5,fontWeight:600}}>
            <span style={{fontSize:13}}>📋</span> Coller depuis le presse-papiers
          </button>
        </div>
        <div onClick={()=>ref.current?.click()}
          onDragOver={e=>{e.preventDefault();setDrag(true);}}
          onDragLeave={()=>setDrag(false)}
          onDrop={e=>{e.preventDefault();setDrag(false);const f=e.dataTransfer.files[0];if(f)read(f);}}
          style={{border:`2px dashed ${drag?"var(--green-dim)":"#ddd"}`,borderRadius:8,cursor:"pointer",overflow:"hidden",
            ...(stretch ? {flex:1} : {height}),
            display:"flex",alignItems:"center",justifyContent:"center",position:"relative",
            background:drag?"rgba(0,255,0,0.04)":"#fafafa",transition:"all 0.15s"}}>
          {value
            ? <img src={value} alt="" style={{maxHeight:"100%",maxWidth:"100%",objectFit:"contain"}}/>
            : <span style={{fontSize:12,color:"#aaa",textAlign:"center",padding:"0 12px"}}>
                JPG, PNG ou SVG<br/>Glisser ici ou cliquer
              </span>}
          {value&&<button onClick={e=>{e.stopPropagation();onChange("");setWarning(null);}}
            style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,0.5)",border:"none",borderRadius:"50%",
              width:22,height:22,cursor:"pointer",color:"#fff",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>}
        </div>
        {warning && <p style={{fontSize:11,color:"#c0392b",marginTop:4,marginBottom:0}}>⚠ {warning}</p>}
        <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml"
          style={{display:"none"}} onChange={e=>{const f=e.target.files?.[0];if(f)read(f);}}/>
      </div>
    </>
  );
}
